import { Box } from '@mui/material'
import {
  attenduEnCaisse,
  dateHeureLisible,
  format,
  money,
  type CashMovement,
  type CashSession,
  type CurrencyCode,
  type StoreMembership,
} from '@iobiz/shared'
import {
  LARGEUR_TICKET_MM,
  MARGE_INTERNE_MM,
  ZONE_SACRIFICE_MM,
} from '../sales/TicketCaisse.js'

/**
 * Rapport de clôture, au format ROULEAU.
 *
 * ⭐ Le document qu'on agrafe au fond de caisse du lendemain. Il doit tenir
 * seul : qui a ouvert, avec quel fond, ce qui est entré, ce qui est sorti, ce
 * qui était attendu, ce qui a été compté. Un écart se conteste des semaines
 * plus tard, et la mémoire du caissier n'est pas une pièce justificative.
 *
 * ⚠️ Mêmes contraintes d'impression que le ticket de vente : 72 mm de large,
 * tout en noir, et le bloc blanc de coupe en fin de document. Les règles de
 * page, elles, sont communes — voir `features/sales/impression.tsx`.
 */

const ENCRE = '#000'

function Filet({ double }: { double?: boolean }): JSX.Element {
  return (
    <Box sx={{ borderTop: `1px ${double ? 'solid' : 'dashed'} ${ENCRE}`, margin: '4px 0' }} />
  )
}

function Ligne({
  gauche,
  droite,
  gras,
  grand,
}: {
  gauche: string
  droite: string
  gras?: boolean
  grand?: boolean
}): JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        gap: 2,
        fontWeight: gras ? 700 : 400,
        fontSize: grand ? '14px' : '11px',
      }}
    >
      <span>{gauche}</span>
      <span style={{ whiteSpace: 'nowrap' }}>{droite}</span>
    </Box>
  )
}

export function TicketCloture({
  session,
  mouvements,
  boutique,
}: {
  session: CashSession
  mouvements: CashMovement[]
  boutique: StoreMembership | undefined
}): JSX.Element {
  const devise = (boutique?.currencyCode ?? 'XAF') as CurrencyCode
  const prix = (v: number): string => format(money(v, devise), 'fr-FR')

  /**
   * ⚠️ L'attendu FIGÉ prime sur le recalcul. Le serveur l'a arrêté au moment
   * du comptage ; une vente arrivée depuis par synchronisation ne doit pas
   * réécrire l'écart constaté ce soir-là. On ne recalcule que pour une session
   * encore ouverte, qui n'a pas d'attendu figé.
   */
  const attendu = session.expectedAmount ?? attenduEnCaisse(session)
  const compte = session.closingCounted
  const ecart = session.variance ?? (compte === null ? null : compte - attendu)

  return (
    <Box
      id="ticket"
      sx={{
        position: 'absolute',
        left: '-200vw',
        top: 0,
        width: `${LARGEUR_TICKET_MM}mm`,
        boxSizing: 'border-box',
        padding: `${MARGE_INTERNE_MM}mm ${MARGE_INTERNE_MM}mm 0`,
        fontFamily: '"Helvetica Neue", Arial, sans-serif',
        fontSize: '11px',
        lineHeight: 1.35,
        color: ENCRE,
        background: '#fff',
        '& *': { color: ENCRE },
      }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <Box sx={{ fontSize: '15px', fontWeight: 700, textTransform: 'uppercase' }}>
          {boutique?.legalName || boutique?.name || 'Boutique'}
        </Box>
        <Box sx={{ fontWeight: 700, marginTop: '4px' }}>RAPPORT DE CAISSE</Box>
      </Box>

      <Filet double />

      <Ligne gauche="Ouverture" droite={dateHeureLisible(session.openedAt)} />
      {session.closedAt && <Ligne gauche="Clôture" droite={dateHeureLisible(session.closedAt)} />}
      {session.openedByName && <Ligne gauche="Ouverte par" droite={session.openedByName} />}

      <Filet />

      <Ligne gauche="Fond de caisse" droite={prix(session.openingFloat)} />
      <Ligne
        gauche={`Ventes espèces (${session.salesCount ?? 0})`}
        droite={prix(session.cashTotal ?? 0)}
      />

      {/* ⭐ Les mouvements sont DÉTAILLÉS, pas résumés en un total. C'est
          précisément cette liste qui explique un écart, et un total agrégé
          renverrait à chercher le détail dans le logiciel — c'est-à-dire
          nulle part, pour qui tient le papier. */}
      {mouvements.length > 0 && (
        <>
          <Filet />
          {mouvements.map((m) => (
            <Box key={m.id}>
              <Ligne
                gauche={m.reason}
                droite={`${m.amount > 0 ? '+' : '−'} ${prix(Math.abs(m.amount))}`}
              />
              <Box sx={{ fontSize: '9px' }}>{dateHeureLisible(m.createdAt)}</Box>
            </Box>
          ))}
        </>
      )}
      <Ligne gauche="Total mouvements" droite={prix(session.movementsTotal ?? 0)} />

      <Filet double />
      <Ligne gauche="ATTENDU" droite={prix(attendu)} gras />
      {compte !== null && <Ligne gauche="COMPTÉ" droite={prix(compte)} gras />}
      {ecart !== null && <Ligne gauche="ÉCART" droite={prix(ecart)} gras grand />}
      <Filet double />

      {session.status === 'open' && (
        <Box sx={{ marginTop: '6px', fontWeight: 700, textAlign: 'center' }}>
          *** CAISSE ENCORE OUVERTE ***
        </Box>
      )}

      {session.notes && <Box sx={{ marginTop: '6px', fontSize: '9px' }}>{session.notes}</Box>}

      {/* Deux signatures : un écart se constate à deux, et c'est ce qui rend
          le document opposable le jour où il est contesté. */}
      <Box sx={{ marginTop: '10px', fontSize: '9px' }}>
        <Box>Caissier : ____________________</Box>
        <Box sx={{ marginTop: '10px' }}>Responsable : ________________</Box>
      </Box>

      <Box sx={{ marginTop: '8px', textAlign: 'center', fontSize: '9px' }}>
        Propulsé par eduqor
      </Box>

      {/* Marge de coupe puis un tiret : voir `TicketCaisse`, même contrainte. */}
      <Box aria-hidden>
        <Box sx={{ height: `${ZONE_SACRIFICE_MM}mm` }} />
        <Box sx={{ fontSize: '9px', textAlign: 'center' }}>-</Box>
      </Box>
    </Box>
  )
}
